import { applyCleValidationErrorsToFormik } from "@/api/cle-api/errors";
import Button from "@/components/bricks/button/Button";
import { CleApiErrorAlert } from "@/components/cle-api/CleApiErrorAlert";
import ValidationErrorMessage from "@/components/formcontrols/ValidationErrorMessage";
import IsRequired from "@/components/formcontrols/common/IsRequired";
import PlusIcon from "@/components/icons/PlusIcon";
import Modal, {
  ModalContext,
  ModalProvider,
  ModalTrigger,
} from "@/components/Modal/Modal";
import Spinner from "@/components/spinner/Spinner";
import { StickyFooter } from "@/components/stickyFooter/modalStickyFooter";
import { UserQueryContext } from "@/context/UserContext";
import { useCreateDispensation } from "@/hooks/cle-hooks";
import { Form, FormikProvider, useFormik } from "formik";
import { useContext, useMemo } from "react";
import * as Yup from "yup";
import { YupNumberOfLessonsRequired } from "../../../../../util/yup";

import type { DecoratedPersonEfteruddannelse } from "./decoratePeriod";
import { formatEfteruddannelsePeriodLabel } from "./dispensationUtils";

interface CreateDispensationModalProps {
  personId: string;
  periods: DecoratedPersonEfteruddannelse[];
}

interface CreateDispensationFormValues {
  externalEfteruddanelseperiodeId: string;
  numberOfLessons: string;
  file: File | null;
}

const initialValues: CreateDispensationFormValues = {
  externalEfteruddanelseperiodeId: "",
  numberOfLessons: "",
  file: null,
};

const validationSchema = Yup.object({
  externalEfteruddanelseperiodeId: Yup.string().required(
    "Efteruddannelsesperiode skal vælges",
  ),
  numberOfLessons: YupNumberOfLessonsRequired(),
  file: Yup.mixed<File>()
    .nullable()
    .required("Dispensation (PDF) skal uploades")
    .test("fileType", "Filen skal være en PDF", (value) => {
      if (!value) return false;

      return (
        value instanceof File &&
        (value.type === "application/pdf" ||
          value.name.toLowerCase().endsWith(".pdf"))
      );
    }),
});

const CreateDispensationForm = ({
  personId,
  periods,
}: CreateDispensationModalProps) => {
  const { setModalOpen } = useContext(ModalContext);
  const {
    mutate,
    isPending,
    isError,
    error,
    reset: resetMutation,
  } = useCreateDispensation();

  const periodOptions = useMemo(
    () =>
      periods.map((period) => ({
        value: period.id,
        label: formatEfteruddannelsePeriodLabel(period),
      })),
    [periods],
  );

  const formik = useFormik<CreateDispensationFormValues>({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      if (!values.file || !values.externalEfteruddanelseperiodeId) return;

      mutate(
        {
          userId: personId,
          externalEfteruddanelseperiodeId:
            values.externalEfteruddanelseperiodeId,
          numberOfLessons: Number(values.numberOfLessons),
          file: values.file,
        },
        {
          onSuccess: () => {
            formik.resetForm();
            resetMutation();
            setModalOpen(false);
          },
          onError: (err) => {
            applyCleValidationErrorsToFormik(err, formik.setFieldError);
          },
        },
      );
    },
  });

  const handleCancel = () => {
    formik.resetForm();
    resetMutation();
    setModalOpen(false);
  };

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={formik.handleSubmit}>
        <div className="c-modal-container">
          <div className="c-item-list c-modal-container-inner">
            <div className="flex-100">
              <label
                className="form-label"
                htmlFor="externalEfteruddanelseperiodeId"
              >
                Efteruddannelsesperiode
                <IsRequired />
              </label>
              <select
                className={`form-select${
                  formik.touched.externalEfteruddanelseperiodeId &&
                  formik.errors.externalEfteruddanelseperiodeId
                    ? " is-invalid"
                    : ""
                }`}
                id="externalEfteruddanelseperiodeId"
                value={formik.values.externalEfteruddanelseperiodeId}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              >
                <option value="">Vælg periode</option>
                {periodOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <ValidationErrorMessage
                touched={formik.touched.externalEfteruddanelseperiodeId}
                errorMessage={formik.errors.externalEfteruddanelseperiodeId}
              />
            </div>
            <div className="flex-100">
              <label className="form-label" htmlFor="numberOfLessons">
                Antal lektioner
                <IsRequired />
              </label>
              <input
                autoComplete="off"
                className={`form-control${
                  formik.touched.numberOfLessons &&
                  formik.errors.numberOfLessons
                    ? " is-invalid"
                    : ""
                }`}
                id="numberOfLessons"
                min="0"
                step="any"
                type="number"
                value={formik.values.numberOfLessons}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              />
              <ValidationErrorMessage
                touched={formik.touched.numberOfLessons}
                errorMessage={formik.errors.numberOfLessons}
              />
            </div>
            <div className="flex-100">
              <label className="form-label" htmlFor="dispensation-file">
                Dispensation (PDF)
                <IsRequired />
              </label>
              <input
                accept="application/pdf,.pdf"
                className={`form-control${
                  formik.touched.file && formik.errors.file ? " is-invalid" : ""
                }`}
                id="dispensation-file"
                type="file"
                onBlur={() => formik.setFieldTouched("file", true)}
                onChange={(event) => {
                  const file = event.currentTarget.files?.[0] ?? null;
                  formik.setFieldValue("file", file, true);
                }}
              />
              {formik.values.file && (
                <p className="text-muted small mb-0 mt-1">
                  {formik.values.file.name}
                </p>
              )}
              <ValidationErrorMessage
                touched={formik.touched.file}
                errorMessage={formik.errors.file}
              />
            </div>
          </div>
          {isError && (
            <div className="c-modal-container-inner mb-2">
              <CleApiErrorAlert
                error={error}
                fallback="Kunne ikke oprette dispensation."
              />
            </div>
          )}
          <StickyFooter>
            <Button color="secondary" type="button" onClick={handleCancel}>
              Annuller
            </Button>
            <div className="d-flex gap-2 align-items-center">
              {isPending && <Spinner size={24} />}
              <Button color="primary" disabled={isPending} type="submit">
                Opret
              </Button>
            </div>
          </StickyFooter>
        </div>
      </Form>
    </FormikProvider>
  );
};

export const CreateDispensationModal = ({
  personId,
  periods,
}: CreateDispensationModalProps) => {
  const { user, hasPermission } = useContext(UserQueryContext);

  if (!hasPermission(user, "TilsynWrite")) {
    return null;
  }

  return (
    <ModalProvider>
      <ModalTrigger
        asChild
        className="btn btn-primary btn--transparent has-icon px-2 justify-content-end"
      >
        <button id="efteruddannelse-opret-dispensation" type="button">
          Opret dispensation
          <span className="icon-bg badge">
            <PlusIcon />
          </span>
        </button>
      </ModalTrigger>
      <Modal title="Opret dispensation">
        <CreateDispensationForm personId={personId} periods={periods} />
      </Modal>
    </ModalProvider>
  );
};
